import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { cn } from "@/libs/utils";
import PointSvg from "./PointSvg.svg";
import {
  type Label,
  OverlayArea,
  Point,
  PreviewType,
} from "@/components/tactsuit/tech-specs";
import { create } from "zustand";
import { useCarousel } from "@/hooks/useCarousel";
import { useWindowResize } from "@/hooks/useWindowResize";
import { Drawer } from "@/v2-components/ui/drawer";
import { useMedia } from "react-use";
import { useTrans } from "@/utils/useTrans";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { RailDetailsLink } from "@/components/tactsuit/components/buy-tactsuit/bundle-detail-rail";

const BREAKPOINT = 1024;
const MOBILE_MIN_RADIUS = 12;
const calcRadius = (radius: number, scales: number[]) =>
  Math.max(MOBILE_MIN_RADIUS, radius * Math.min(scales[0], scales[1]));
interface MotorsType {
  canvasSize: number[];
  origin: number[];
  points: OverlayArea[];
}
interface ControllerType {
  title?: string;
  imageSrc: string;
  imageWidth: number;
  imageHeight: number;
  descriptions: string[];
}
interface ImageType extends Omit<ControllerType, "descriptions" | "title"> {
  mobileImageWidth: number;
  mobileImageHeight: number;
  label: string;
}

const MOTORS: Record<
  string,
  Record<string, MotorsType[]>
> = require("./motors.json");
const CONTROLLERS: Record<
  string,
  Record<string, Record<string, ControllerType>>
> = require("./controllers.json");

const IMAGES: Record<
  string,
  Record<
    string,
    Record<
      string,
      {
        canvasSize: number[];
        origin: number[];
        points: Point[];
        images: ImageType[];
      }
    >
  >
> = require("./images.json");

type Current = {
  side: string;
  point: Point | null;
  /** 툴팁 미리보기에서 누른 점의 화면 위치 — 툴팁을 그 아래(공간 없으면 위)에 띄운다 */
  rect?: DOMRect;
};

type Store = {
  slug: string;
  setSlug: (slug: string) => void;
  scales: number[];
  canvasSize: [number, number];
  current: Current | null;
  setCurrent: (current: Current | null) => void;
  setScales: (scales: number[]) => void;
  setCanvasSize: (canvasSize: [number, number]) => void;
};

const useStore = create<Store>()((set) => ({
  scales: [1, 1],
  current: null,
  slug: "",
  canvasSize: [Infinity, Infinity],
  setSlug: (slug) => set({ slug }),
  setCurrent: (current) => set({ current }),
  setScales: (scales) => set({ scales }),
  setCanvasSize: (canvasSize) => set({ canvasSize }),
}));

export default function TechSpecsPreview({
  slug,
  previews,
}: {
  previews: PreviewType[];
  slug: string;
}) {
  const { t } = useTrans(["tactsuit"]);
  const { carouselApi, carouselRef, current } = useCarousel({ loop: true });
  const setCurrent = useStore((store) => store.setCurrent);
  // 캡션이 있는 미리보기(EXOSHOCK 번들 — `PreviewType.caption`)만 시안 배치를 따른다. 없으면 종전 그대로.
  const hasCaption = previews.some((p) => !!p.caption);
  // 점에 툴팁이 있는 미리보기(EXOSHOCK 번들)는 마우스오버 라벨 · 모터 원 대신 「누르면 툴팁」만 쓴다(시안 31769:39176)
  const tooltipMode = previews.some((p) => p.points.some((pt) => !!pt.tooltip));
  const setSlug = useStore((store) => store.setSlug);

  useWindowResize(() => {
    setCurrent(null);
  });

  useEffect(() => {
    setSlug(slug);
  }, [slug]);

  return (
    <>
      {/* desktop */}
      {/* 캡션이 있으면 py 24 · 캡션은 이미지 바로 아래 16/22 ls .5(피그마 31584:38650) */}
      <div
        className={cn(
          "hidden rounded-2xl bg-coolgray-10 laptop:block",
          hasCaption ? "py-6" : "py-10",
        )}
      >
        <div className="relative flex flex-nowrap justify-center gap-x-6">
          {previews.map((preview, index) =>
            preview.caption ? (
              <div key={index} className="flex flex-col items-center">
                <Preview
                  index={index}
                  preview={preview}
                  tooltipMode={tooltipMode}
                />
                <p className="text-center text-lg tracking-[0.5px] text-coolgray-90">
                  {t(preview.caption)}
                </p>
              </div>
            ) : (
              <Preview
                index={index}
                preview={preview}
                tooltipMode={tooltipMode}
                key={index}
              />
            ),
          )}
        </div>
      </div>
      {/* mobile */}
      <div className="block laptop:hidden">
        <div className="rounded-2xl bg-coolgray-10">
          <div ref={carouselRef} className="overflow-hidden">
            <div className="flex">
              {previews.map((preview, index) => (
                <div
                  key={index}
                  className="relative flex w-full shrink-0 grow-0 justify-center p-4"
                >
                  <div>
                    {/* 캡션이 있으면 면 이름 대신 미리보기 **아래**(회색 박스 밖)에 쓴다 */}
                    {!preview.caption && (
                      <h4 className="text-center text-sm">{preview.side}</h4>
                    )}
                    <Preview
                      index={index}
                      preview={preview}
                      tooltipMode={tooltipMode}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* 모바일 캡션 14/18 ls .25 · 박스와 20(피그마 31602:38900) */}
        {hasCaption && (
          <p className="mt-5 text-center text-base tracking-[0.25px] text-coolgray-90">
            {previews[current]?.caption ? t(previews[current].caption!) : ""}
          </p>
        )}
        <Pagination
          onClick={(index: number) => carouselApi?.scrollTo(index)}
          currentPage={current}
        />
      </div>
      {tooltipMode && <SpecTooltip />}
    </>
  );
}

const useSize = () => {
  const [size, setSize] = useState([0, 0]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const observerRef = ref.current;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize([width, height]);
    });

    observer.observe(observerRef);

    return () => {
      if (observerRef) {
        observer.unobserve(observerRef);
      }
    };
  }, [ref]);

  return { size, ref };
};

const Preview = ({
  preview,
  index,
  tooltipMode,
}: {
  preview: PreviewType;
  index: number;
  tooltipMode: boolean;
}) => {
  const { side, imageSrc, canvasSize, points } = preview;
  const setScales = useStore((store) => store.setScales);
  const setCurrent = useStore((store) => store.setCurrent);
  const setCanvasSize = useStore((store) => store.setCanvasSize);
  const { size, ref } = useSize();
  const scales = [size[0] / canvasSize[0], size[1] / canvasSize[1]];

  useEffect(() => {
    setCanvasSize(canvasSize);
  }, [canvasSize]);

  useEffect(() => {
    const updated = scales[0] > 0 && scales[1] > 0;
    if (!updated) {
      return;
    }
    setScales(scales);
  }, [scales]);

  return (
    <div ref={ref} className={cn("relative")}>
      <Image
        width={canvasSize[0]}
        height={canvasSize[1]}
        src={imageSrc}
        alt={side}
        className="object-cover"
      />
      {!tooltipMode && (
        <Detail
          onClick={(e) => {
            e.stopPropagation();
            // 모바일에서 클릭 시 현재 포인트를 설정
            if (window.innerWidth <= BREAKPOINT) {
              setCurrent(null);
            }
          }}
          side={side}
          onMouseLeave={(e) => {
            e.stopPropagation();
            // 데스크탑에서 마우스가 벗어날 때 현재 포인트를 초기화
            if (window.innerWidth > BREAKPOINT) {
              setCurrent(null);
            }
          }}
          index={index}
        />
      )}
      {tooltipMode && <TooltipMotors index={index} />}
      <Pointers points={points} side={side} tooltipMode={tooltipMode} />
    </div>
  );
};

const Pointers = ({
  points,
  side,
  tooltipMode,
}: {
  points: Point[];
  side: string;
  tooltipMode: boolean;
}) => {
  const { current, setCurrent, scales } = useStore();

  return (
    <div className="absolute left-0 top-0 z-10">
      {points.map((point, _index) => {
        const x = point.x * scales[0];
        const y = point.y * scales[1];
        const radius = calcRadius(point.r, scales);
        const isCurrent =
          current?.point?.label === point.label && current.side === side;

        const style = {
          left: `${x}px`,
          top: `${y}px`,
          width: `${radius * 2}px`,
          height: `${radius * 2}px`,
        };

        return (
          <div
            className="absolute flex items-center"
            style={style}
            key={_index}
            {...(tooltipMode && {
              role: "button",
              "aria-label": point.label,
              "aria-expanded": isCurrent,
              "data-spec-point": "",
            })}
            onClick={(e) => {
              if (tooltipMode) {
                // 같은 점을 다시 누르면 닫는다(기획 HTML 과 같은 토글)
                setCurrent(
                  isCurrent
                    ? null
                    : {
                        point,
                        side,
                        rect: e.currentTarget.getBoundingClientRect(),
                      },
                );
                return;
              }
              if (window.innerWidth <= BREAKPOINT) {
                setCurrent(isCurrent ? null : { point, side });
              }
            }}
            onMouseEnter={(e) => {
              if (!tooltipMode && window.innerWidth > BREAKPOINT) {
                setCurrent({ point, side });
              }
            }}
          >
            <div
              className={cn(
                "relative size-full animate-pulse cursor-pointer",
                !tooltipMode && "laptop:cursor-default",
              )}
            >
              <Pointer alt={point.label} />
            </div>
            {isCurrent && (
              <Pointer
                className="absolute left-0 top-0 z-10"
                alt={point.label}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

function Detail({
  onClick,
  index,
  side,
  onMouseLeave,
}: React.HTMLAttributes<HTMLDivElement> & {
  index: number;
  side: string;
  onClick: (e: React.MouseEvent) => void;
  onMouseLeave: (e: React.MouseEvent) => void;
}) {
  const current = useStore((store) => store.current);
  const scales = useStore((store) => store.scales);
  const slug = useStore((store) => store.slug);

  if (!current || !current.point) {
    return null;
  }
  const { point } = current;
  const motors = MOTORS?.[slug]?.[current?.point?.label ?? ""]?.[index];
  const controller = CONTROLLERS?.[slug]?.[side]?.[point.label];
  const images = IMAGES?.[slug]?.[side]?.[point.label];
  const title = slug.split("-").join(" ").toLowerCase();
  const canShowLabel =
    !!current.point &&
    side === current.side &&
    !controller &&
    // !motors &&
    !images;

  return (
    <>
      {!!controller && <Controller controller={controller} />}
      {!!images && (
        <Images title={title} images={images.images} origin={images.origin} />
      )}
      <div onClick={onClick} className="absolute left-0 top-0 z-10">
        {canShowLabel && (
          <Label onMouseLeave={onMouseLeave} point={current.point} />
        )}
        {!!motors && (
          <Motors onMouseLeave={onMouseLeave} scales={scales} motors={motors} />
        )}
      </div>
    </>
  );
}

const Controller = ({ controller }: { controller: ControllerType }) => {
  const isMobile = useMedia(`(max-width: ${BREAKPOINT}px)`, true);
  const { current, setCurrent, scales, slug } = useStore((store) => ({
    current: store.current,
    slug: store.slug,
    setCurrent: store.setCurrent,
    scales: store.scales,
  }));

  if (!current || !current.point) {
    return null;
  }

  const { point } = current;
  const radius = calcRadius(point.r, scales);
  const title = slug.split("-").join(" ").toLowerCase();
  const style = {
    left: `${point.x * scales[0] + 2 * radius}px`,
    top: `${point.y * scales[1]}px`,
  };

  return (
    <>
      <div style={style} className="absolute left-1 z-20 hidden laptop:block">
        <MainController
          className={"left-1"}
          onMouseLeave={() => setCurrent(null)}
          title={controller.title}
          label={point.label}
          descriptions={controller.descriptions}
          imageSrc={controller.imageSrc}
          width={controller.imageWidth}
          height={controller.imageHeight}
        />
      </div>
      {isMobile && (
        <div className="block laptop:hidden">
          <BottomDrawer
            title={`${title} Specs`}
            onOpenChange={() => setCurrent(null)}
          >
            {!!controller && (
              <MainController
                title={controller.title}
                label={point.label}
                descriptions={controller.descriptions}
                imageSrc={controller.imageSrc}
                width={controller.imageWidth}
                height={controller.imageHeight}
              />
            )}
          </BottomDrawer>
        </div>
      )}
    </>
  );
};

const BottomDrawer = ({
  children,
  title,
  onOpenChange,
  open = true,
}: React.PropsWithChildren<{
  onOpenChange: () => void;
  title: string;
  open?: boolean;
}>) => (
  <Drawer
    overlayClassName="bg-black/40"
    onOpenChange={onOpenChange}
    side="bottom"
    open={open}
    className="flex h-auto max-h-[90%] flex-col overflow-hidden !p-0"
  >
    <div className="flex items-center justify-between px-4 py-7">
      <p className="text-2xl capitalize">{title}</p>
      <button onClick={onOpenChange} className="p-2.5 laptop:p-0">
        <CrossIcon />
      </button>
    </div>
    <div className="grow overflow-auto">{children}</div>
  </Drawer>
);

const Images = ({
  images,
  origin,
  title,
}: {
  title: string;
  images: ImageType[];
  origin: number[];
}) => {
  const isMobile = useMedia(`(max-width: ${BREAKPOINT}px)`, true);
  const { scales, setCurrent, current } = useStore((store) => ({
    scales: store.scales,
    setCurrent: store.setCurrent,
    current: store.current,
  }));

  if (!current || !current.point) {
    return null;
  }

  return (
    <>
      <div
        style={{
          left: `${origin[0] * scales[0]}px`,
          top: `${origin[1] * scales[1]}px`,
        }}
        onMouseLeave={() => {
          setCurrent(null);
        }}
        className="absolute left-0 top-0 z-20 hidden laptop:block"
      >
        <div className="flex flex-col items-center gap-y-6 rounded-2xl bg-white p-6 shadow-sm">
          <div>{current.point.label}</div>
          <div className="flex-no-wrap relative flex gap-x-6">
            {images.map((image, index) => {
              const { imageHeight, imageSrc, imageWidth, label } = image;
              return (
                <ImageCard
                  key={index}
                  imageSrc={imageSrc}
                  width={imageWidth * scales[0]}
                  height={imageHeight * scales[1]}
                  label={label}
                />
              );
            })}
          </div>
        </div>
      </div>
      {isMobile && (
        <div className="block laptop:hidden">
          <BottomDrawer
            open={isMobile}
            title={`${title} Specs`}
            onOpenChange={() => setCurrent(null)}
          >
            <div className="relative flex flex-col items-center justify-center gap-x-6 gap-y-10 rounded-2xl bg-white p-6 shadow-sm">
              {images.map((image, index) => {
                const { imageSrc, label, mobileImageHeight, mobileImageWidth } =
                  image;
                return (
                  <ImageCard
                    key={index}
                    imageSrc={imageSrc}
                    width={mobileImageWidth * scales[0]}
                    height={mobileImageHeight * scales[1]}
                    label={label}
                  />
                );
              })}
            </div>
          </BottomDrawer>
        </div>
      )}
    </>
  );
};

const ImageCard = ({
  imageSrc,
  width,
  height,
  label,
  className,
}: {
  imageSrc: string;
  width: number;
  height: number;
  label: string;
  className?: string;
}) => {
  return (
    <div className="flex flex-col items-center gap-y-5">
      <Image
        src={imageSrc}
        alt={label}
        width={width}
        height={height}
        style={{ minWidth: `${width}px` }}
        className={cn("object-cover", className)}
      />
      <p className="text-center text-base">{label}</p>
    </div>
  );
};

const MainController = ({
  title,
  label,
  className,
  imageSrc,
  width,
  height,
  descriptions = [],
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  label: string;
  title?: string;
  imageSrc: string;
  width: number;
  height: number;
  descriptions?: string[];
}) => (
  <div
    {...rest}
    className={cn(
      "relative rounded-2xl bg-white p-4 text-sm text-coolgray-90 laptop:p-6 laptop:shadow-sm",
      className,
    )}
  >
    <div className="flex flex-col items-center">
      {title && (
        <div className="flex justify-center">
          <p>{title}</p>
        </div>
      )}
      <Image
        style={{
          minWidth: `${width}px`,
          minHeight: `${height}px`,
          maxWidth: `${width}px`,
        }}
        className="mt-5 object-cover"
        width={width}
        height={height}
        alt=""
        src={imageSrc}
      />
      {descriptions.length > 0 && (
        <div
          style={{ maxWidth: `${width}px` }}
          className="mt-3 grid w-full auto-cols-fr grid-flow-col gap-x-2"
        >
          {descriptions.map((description, index) => (
            <p
              className={cn([
                index % 2 === 0 ? "text-left" : "text-right",
                "whitespace-pre",
              ])}
              key={index}
            >
              {description}
            </p>
          ))}
        </div>
      )}
      <p className="mt-5 text-center text-base">{label}</p>
    </div>
  </div>
);

const Label = ({
  point,
  onMouseLeave,
}: {
  point: Point;
  onMouseLeave: (e: React.MouseEvent) => void;
}) => {
  const scales = useStore((store) => store.scales);
  const radius = calcRadius(point.r, scales);
  const canvasSize = useStore((store) => store.canvasSize);
  const center = canvasSize.map((size) => (size / 2) * scales[0]);
  const left = point.x * scales[0];
  let top = point.y * scales[1];
  const isOverlapped = point.label.startsWith("Haptic Motor");
  const transform =
    left > center[0] || isOverlapped
      ? `translate(calc(-100% + ${radius * 2}px), 0%)`
      : "unset";
  const padding = isOverlapped
    ? `0 ${radius * 2 + 2}px 0 4px`
    : left > center[0]
      ? `0 ${2 * radius + 4}px 0 4px`
      : `0 4px 0 ${2 * radius + 4}px`;

  return (
    <div
      style={{
        left: `${left}px`,
        top: `${top}px`,
        height: `${radius * 2}px`,
      }}
      className="absolute z-10 min-h-5.5 laptop:min-h-7"
    >
      <div
        onMouseLeave={onMouseLeave}
        className="relative flex size-full items-center"
      >
        <div
          style={{
            transform,
            padding,
          }}
          className="top-0 flex h-full items-center whitespace-nowrap rounded-full bg-white/90 text-[11px] laptop:text-base"
        >
          <span className="px-2">{point.label}</span>
        </div>
      </div>
    </div>
  );
};

const Pointer = ({
  className,
  alt = "",
  ...rest
}: React.HTMLAttributes<HTMLImageElement> & { alt?: string }) => (
  <Image
    alt={alt}
    {...rest}
    className="min-h-5.5 min-w-5.5 laptop:min-h-7 laptop:min-w-7"
    src={PointSvg}
    fill
  />
);

const Motors = ({
  scales,
  motors,
  onMouseLeave,
  children,
}: React.PropsWithChildren<{
  motors: { canvasSize: number[]; origin: number[]; points: OverlayArea[] };
  scales: number[];
  onMouseLeave: (e: React.MouseEvent) => void;
}>) => {
  const maxX = Math.max(
    ...motors.points.map((p) => {
      if (p.type === "rect") {
        return p.x + (p.width || 0);
      }
      return p.x + calcRadius(p.r, scales) * 2;
    }),
  );
  const maxY = Math.max(
    ...motors.points.map((p) => {
      if (p.type === "rect") {
        return p.y + (p.height || 0);
      }
      return p.y + calcRadius(p.r, scales) * 2;
    }),
  );

  return (
    <div
      style={{
        width: `${maxX * scales[0]}px`,
        height: `${maxY * scales[1]}px`,
        left: `${motors.origin[0] * scales[0]}px`,
        top: `${motors.origin[1] * scales[1]}px`,
      }}
      onMouseLeave={onMouseLeave}
      className={"absolute"}
    >
      {motors.points.map((point, i) => {
        if (point.type === "rect") {
          const width = (point.width || 0) * scales[0];
          const height = (point.height || 0) * scales[1];

          return (
            <div
              key={i}
              className="border-white-500 absolute border-[2px] border-dashed bg-white/30"
              style={{
                left: `${point.x * scales[0]}px`,
                top: `${point.y * scales[1]}px`,
                width: `${width}px`,
                height: `${height}px`,
              }}
            />
          );
        }

        return (
          <Motor
            key={i}
            x={point.x * scales[0]}
            y={point.y * scales[1]}
            r={calcRadius(point.r, scales)}
          />
        );
      })}
      {children}
    </div>
  );
};

const Motor = ({ x, y, r }: { x: number; y: number; r: number }) => {
  return (
    <div
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${r * 2}px`,
        height: `${r * 2}px`,
      }}
      className="absolute z-0"
    >
      <div className="relative flex size-full items-center rounded-full border border-dashed bg-white/50" />
    </div>
  );
};

const Pagination = ({
  onClick,
  currentPage,
}: {
  onClick: (nextPage: number) => void;
  currentPage: number;
}) => (
  <div className="mt-5 flex items-center justify-center gap-x-2 laptop:hidden">
    {[0, 1].map((_, index) => (
      <button
        key={index}
        onClick={() => onClick(index)}
        className={cn([
          "h-0.5 w-5 rounded-sm hover:bg-coolgray-hover-50",
          currentPage === index
            ? "pointer-events-none bg-coolgray-50"
            : "bg-coolgray-20",
        ])}
      />
    ))}
  </div>
);

/**
 * 툴팁 미리보기에서 모터 점(「16 Haptic Points」)이 열려 있으면 앞 · 뒤 모두에 모터 원을 그린다 — 좌표는 motors.json.
 * 원은 보기만 한다(`pointer-events-none`) — 원 위를 눌러도 바깥 클릭으로 닫힌다.
 */
const TooltipMotors = ({ index }: { index: number }) => {
  const current = useStore((store) => store.current);
  const scales = useStore((store) => store.scales);
  const slug = useStore((store) => store.slug);
  const motors =
    current?.point?.tooltip && MOTORS?.[slug]?.[current.point.label]?.[index];
  if (!motors) return null;
  return (
    <div
      data-testid="tooltip-motors"
      className="pointer-events-none absolute left-0 top-0 z-10"
    >
      <Motors onMouseLeave={() => {}} scales={scales} motors={motors} />
    </div>
  );
};

/** 툴팁 위치 — 점 아래 8(공간 없으면 위) · 점 가운데 맞춤 · 화면 좌우 16 안(시안 `SpecTooltip` 설명 · 기획 HTML 같은 계산) */
const TOOLTIP_GAP = 8;
const TOOLTIP_VIEW_MARGIN = 16;

/**
 * 스펙 점 툴팁 — 제목 16/24 w500 · 설명 14/20 coolgray-60 · (같은 페이지 섹션 링크) · 너비 300 · pl24 pr16 py20 · gap 8
 * (시안 `SpecTooltip` 31769:39163). 닫기 = × · 바깥 클릭 · 스크롤 · 리사이즈(리사이즈는 위 `useWindowResize`).
 *
 * ⚠️ `body` 로 포털한다 — 모바일 캐러셀은 `transform` 을 쓰므로 그 안의 `fixed` 는 화면이 아니라 캐러셀 기준이 된다.
 */
const SpecTooltip = () => {
  const { t } = useTrans(["tactsuit"]);
  const current = useStore((store) => store.current);
  const setCurrent = useStore((store) => store.setCurrent);
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>();
  const tooltip = current?.point?.tooltip;
  const rect = current?.rect;

  useLayoutEffect(() => {
    if (!tooltip || !rect || !ref.current) {
      setPosition(undefined);
      return;
    }
    const box = ref.current.getBoundingClientRect();
    const viewWidth = document.documentElement.clientWidth;
    const viewHeight = document.documentElement.clientHeight;
    const left = Math.max(
      TOOLTIP_VIEW_MARGIN,
      Math.min(
        viewWidth - box.width - TOOLTIP_VIEW_MARGIN,
        rect.left + rect.width / 2 - box.width / 2,
      ),
    );
    const below = rect.bottom + TOOLTIP_GAP;
    const top =
      below + box.height < viewHeight - TOOLTIP_VIEW_MARGIN
        ? below
        : Math.max(TOOLTIP_VIEW_MARGIN, rect.top - box.height - TOOLTIP_GAP);
    setPosition({ left, top });
  }, [tooltip, rect]);

  useEffect(() => {
    if (!tooltip) return;
    const close = () => setCurrent(null);
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      // 점 자체는 그 점의 onClick 이 토글한다
      if (
        ref.current?.contains(target) ||
        target?.closest?.("[data-spec-point]")
      )
        return;
      close();
    };
    window.addEventListener("scroll", close, { passive: true });
    document.addEventListener("click", onDocumentClick);
    return () => {
      window.removeEventListener("scroll", close);
      document.removeEventListener("click", onDocumentClick);
    };
  }, [tooltip, setCurrent]);

  if (!tooltip || !rect) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={t(tooltip.title)}
      style={
        position
          ? { left: position.left, top: position.top }
          : { left: 0, top: 0, visibility: "hidden" }
      }
      className="fixed z-50 flex w-[300px] flex-col gap-2 rounded-xl border border-coolgray-20 bg-white py-5 pl-6 pr-4 drop-shadow-[0px_8px_15px_rgba(33,39,42,0.14)]"
    >
      <div className="flex items-start gap-2">
        <p className="flex-1 text-lg font-500 leading-6 text-coolgray-90">
          {t(tooltip.title)}
        </p>
        <button
          type="button"
          aria-label="Close"
          onClick={() => setCurrent(null)}
          className="shrink-0 text-coolgray-60"
        >
          {/* 시안 `Close` 아이콘 경로 그대로(둥근 × — 리포 아이콘 중 같은 모양이 없다) */}
          <svg viewBox="0 0 20 20" className="size-5" aria-hidden>
            <path
              d="M10 11.0625L6.58334 14.4791C6.43056 14.6319 6.25695 14.7048 6.06251 14.6979C5.86806 14.6909 5.69445 14.6111 5.54167 14.4583C5.38889 14.3055 5.31251 14.1284 5.31251 13.927C5.31251 13.7257 5.38889 13.5486 5.54167 13.3958L8.93751 9.99996L5.52084 6.58329C5.36806 6.43051 5.29514 6.25343 5.30209 6.05204C5.30903 5.85065 5.38889 5.67357 5.54167 5.52079C5.69445 5.36801 5.87153 5.29163 6.07292 5.29163C6.27431 5.29163 6.45139 5.36801 6.60417 5.52079L10 8.93746L13.4167 5.52079C13.5694 5.36801 13.7465 5.29163 13.9479 5.29163C14.1493 5.29163 14.3264 5.36801 14.4792 5.52079C14.6319 5.67357 14.7083 5.85065 14.7083 6.05204C14.7083 6.25343 14.6319 6.43051 14.4792 6.58329L11.0625 9.99996L14.4792 13.4166C14.6319 13.5694 14.7083 13.743 14.7083 13.9375C14.7083 14.1319 14.6319 14.3055 14.4792 14.4583C14.3264 14.6111 14.1493 14.6875 13.9479 14.6875C13.7465 14.6875 13.5694 14.6111 13.4167 14.4583L10 11.0625Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      <p className="text-base leading-5 text-coolgray-60">{t(tooltip.copy)}</p>
      {tooltip.link && (
        <div className="pt-1">
          <RailDetailsLink
            label={t(tooltip.link.label)}
            anchor={tooltip.link.anchor}
          />
        </div>
      )}
    </div>,
    document.body,
  );
};
